<script setup>
import { reactive, watch } from 'vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Textarea from 'dashboard/components-next/textarea/TextArea.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  productInfo: {
    type: Object,
    default: () => ({
      series: '',
      description: '',
      overview: '',
      keyIngredients: '',
      functions: '',
      targetAudience: '',
      usage: '',
      packaging: '',
    }),
  },
  isEditable: {
    type: Boolean,
    default: true,
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update', 'save']);

const localData = reactive({ ...props.productInfo });

// Watch for external changes
watch(
  () => props.productInfo,
  newValue => {
    Object.assign(localData, newValue);
  },
  { deep: true }
);

// Emit changes
watch(
  localData,
  newValue => {
    emit('update', { ...newValue });
  },
  { deep: true }
);

const handleSave = () => {
  emit('save', { ...localData });
};

const fields = [
  { key: 'series', label: 'SERIES', type: 'input', placeholder: 'e.g., Aemis' },
  {
    key: 'description',
    label: 'DESCRIPTION',
    type: 'textarea',
    placeholder: 'Detailed product description...',
    rows: 4,
  },
  {
    key: 'overview',
    label: 'OVERVIEW',
    type: 'textarea',
    placeholder: 'Brief product overview...',
    rows: 2,
  },
  {
    key: 'keyIngredients',
    label: 'KEY INGREDIENTS',
    type: 'textarea',
    placeholder: 'List key ingredients...',
    rows: 2,
  },
  {
    key: 'functions',
    label: 'FUNCTIONS',
    type: 'textarea',
    placeholder: 'Product functions and benefits...',
    rows: 2,
  },
  {
    key: 'targetAudience',
    label: 'TARGET AUDIENCE',
    type: 'textarea',
    placeholder: 'Who is this product for...',
    rows: 2,
  },
  {
    key: 'usage',
    label: 'USAGE INSTRUCTIONS',
    type: 'textarea',
    placeholder: 'How to use the product...',
    rows: 2,
  },
  {
    key: 'packaging',
    label: 'PACKAGING',
    type: 'input',
    placeholder: 'e.g., 30 sachets per box',
  },
];
</script>

<template>
  <div class="flex flex-col gap-6">
    <!-- Fields -->
    <div v-for="field in fields" :key="field.key" class="flex flex-col gap-2">
      <label
        class="text-xs font-semibold text-n-slate-11 uppercase tracking-wide"
      >
        {{ field.label }}
      </label>

      <!-- Input -->
      <Input
        v-if="field.type === 'input'"
        v-model="localData[field.key]"
        :placeholder="field.placeholder"
        :disabled="!isEditable"
        class="w-full"
      />

      <!-- Textarea -->
      <Textarea
        v-else-if="field.type === 'textarea'"
        v-model="localData[field.key]"
        :placeholder="field.placeholder"
        :rows="field.rows || 3"
        :disabled="!isEditable"
        class="w-full"
      />

      <!-- Display mode (non-editable) -->
      <div
        v-if="!isEditable && localData[field.key]"
        class="text-sm text-n-slate-12 leading-relaxed"
      >
        {{ localData[field.key] }}
      </div>
    </div>

    <!-- Save Button -->
    <div
      v-if="isEditable"
      class="flex justify-end pt-4 border-t border-n-slate-3"
    >
      <Button
        color="primary"
        :loading="isLoading"
        label="Save Information"
        class="!bg-woot-500 hover:!bg-woot-600"
        @click="handleSave"
      />
    </div>
  </div>
</template>
